import { create } from 'zustand';

interface SelectionState {
  // State
  selectedIds: string[];
  hoveredId: string | null;
  focusedId: string | null;

  // Actions
  select: (id: string) => void;
  multiSelect: (ids: string[]) => void;
  addToSelection: (id: string) => void;
  removeFromSelection: (id: string) => void;
  toggleSelection: (id: string) => void;
  clearSelection: () => void;
  selectAll: (ids: string[]) => void;
  setHovered: (id: string | null) => void;
  setFocused: (id: string | null) => void;

  // Helpers
  isSelected: (id: string) => boolean;
  hasSelection: () => boolean;
  getSelectionCount: () => number;
}

export const useSelectionStore = create<SelectionState>((set, get) => ({
  // Initial state
  selectedIds: [],
  hoveredId: null,
  focusedId: null,

  // Actions
  select: (id) => set({ selectedIds: [id], focusedId: id }),

  multiSelect: (ids) => set({ selectedIds: ids, focusedId: ids[0] ?? null }),

  addToSelection: (id) =>
    set((state) => ({
      selectedIds: state.selectedIds.includes(id)
        ? state.selectedIds
        : [...state.selectedIds, id],
      focusedId: id,
    })),

  removeFromSelection: (id) =>
    set((state) => {
      const newSelection = state.selectedIds.filter((i) => i !== id);
      return {
        selectedIds: newSelection,
        focusedId:
          state.focusedId === id ? newSelection[0] ?? null : state.focusedId,
      };
    }),

  toggleSelection: (id) =>
    set((state) => {
      const isCurrentlySelected = state.selectedIds.includes(id);
      if (isCurrentlySelected) {
        const newSelection = state.selectedIds.filter((i) => i !== id);
        return {
          selectedIds: newSelection,
          focusedId:
            state.focusedId === id ? newSelection[0] ?? null : state.focusedId,
        };
      } else {
        return {
          selectedIds: [...state.selectedIds, id],
          focusedId: id,
        };
      }
    }),

  clearSelection: () => set({ selectedIds: [], focusedId: null }),

  selectAll: (ids) => set({ selectedIds: ids, focusedId: ids[0] ?? null }),

  setHovered: (id) => set({ hoveredId: id }),

  setFocused: (id) => set({ focusedId: id }),

  // Helpers (these read from state, use get())
  isSelected: (id) => get().selectedIds.includes(id),

  hasSelection: () => get().selectedIds.length > 0,

  getSelectionCount: () => get().selectedIds.length,
}));
